import { useQuery } from '@apollo/client/react';

import {
  DoorToDoor__BundleEnum,
  StorageBundlesDocument,
} from '@graphql/platform';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { PLANS } from '@root/constants/pricing';

export type ChallengerPackingAmountState = {
  amount: number | undefined;
  loading: boolean;
  error?: Error;
};

/**
 * Full-service packing price for the selected plan size + ZIP.
 *
 * Fetches StorageBundles directly so the amount tracks size changes immediately
 * and is not blocked (or briefly wrong) when usePricingSummary nulls during
 * appointment-quote refetch.
 */
export const useChallengerPackingAmount = (): ChallengerPackingAmountState => {
  const {
    flowState: {
      values: { planSize, zip },
    },
  } = useStorageCheckoutContext();

  const cuft = planSize ? PLANS[planSize].cuft : undefined;
  const skip = cuft == null || !zip;

  const { data, loading, error } = useQuery(StorageBundlesDocument, {
    variables: {
      cuft,
      amountCuft: cuft ?? 0,
      zip,
    },
    skip,
  });

  const packingBundle = data?.bundles?.find(
    (bundle) => bundle.kind === DoorToDoor__BundleEnum.PickupAndPacking,
  );

  return {
    amount: packingBundle?.packageSetEntry?.amount ?? undefined,
    loading: !skip && loading,
    error: error,
  };
};
